import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

import { BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import DatadogDashboardService from "./datadogDashboardService";
import { DatadogGraphs, DatadogGraphsApiResponse, graphCategories } from "./types";
import { UiStatisticsCollapse } from "../shared/UiStastiticsCollapse";
import { UiStastiticsCollapseContextProvider, UiStatisticsCollapseContext } from "../contexts/UiStatisticsCollapseContext";
import { Roles, UserContext } from "@app/user";
import { Unauthorized } from "@app/shared/ErrorPages";

/* eslint-disable sort-keys */
const timeframeStrings = {
  "1_hour": "1 Hour",
  "4_hours": "4 Hours",
  "1_day": "1 Day",
  "2_days": "2 Days",
  "1_week": "1 Week",
};
/* eslint-enable */

const UiStatisticsContent: React.FC<React.PropsWithChildren<unknown>> = () => {
  const user = useContext(UserContext);
  if (!user.roles.includes(Roles.PERFORMANCE_MONITOR)) {
    return <Unauthorized />;
  }
  // store timeframe data
  const [timeframe, setTimeframe] = useState("1_day");
  const [iframes, setIframes] = useState<DatadogGraphs>({});
  const { collapseAll, expandedCategories } = useContext(UiStatisticsCollapseContext);
  const datadogDashboardService = useRef(new DatadogDashboardService()).current;

  const timeframeOptions = useRef(Object.entries(timeframeStrings).map(([key, value]) => <option key={key} value={key}>{value}</option>)).current;

  useEffect(() => {
    const graphRequests: Promise<DatadogGraphsApiResponse[]>[] = [];
    for (const cat of expandedCategories) {
      const catNumber = graphCategories[cat];
      graphRequests.push(datadogDashboardService.getGraphsForClient(timeframe, catNumber));
    }
    Promise.all(graphRequests).then((results: Array<DatadogGraphsApiResponse[]>) => {
      // the results are unlabeled but will be in the same order as the expandedCategories
      const newIframes = { ...iframes };
      results.forEach((r, index) => {
        newIframes[expandedCategories[index]] = r;
      });
      setIframes(newIframes);
    });
  }, [timeframe]);

  const fetchCategory = (category: string) => {
    const catNumber = graphCategories[category];
    datadogDashboardService.getGraphsForClient(timeframe, catNumber).then((result) => {
      const newIframes = { ...iframes };
      newIframes[category] = result;
      setIframes(newIframes);
    });
  }

  const handleQueryCopy = (target: HTMLElement, query: string) => {
    navigator.clipboard.writeText(query).then(() => {
      // if you don't save the target to a local variable it can get wiped from memory before setTimeout triggers
      const button = target;
      button.innerHTML = "&check; Query Copied";
      window.setTimeout(() => {
        button.innerHTML = "Copy Query";
      }, 1500);
    });
  }

  const categoryCollapses = useMemo(() => {
    return Object.keys(graphCategories).map((cat, collapseIndex) => {
      const graphElements = iframes[cat]?.length ? iframes[cat].map((iframe, index) => (
        <span key={index} className="position-relative d-inline-block">
          <QESButton
            qesType="secondary"
            qesTestId={`ui-statistics__copy-graph--${cat}-${index}`}
            className="ui-statistics__copy-graph-button"
            onClick={(e) => handleQueryCopy(e.target as HTMLElement, iframe.graphQuery)}
          >
            Copy Query
          </QESButton>
          <span dangerouslySetInnerHTML={{ __html: iframe.embedUrl }} />
        </span>
      )) : null;
      return <UiStatisticsCollapse key={collapseIndex} title={cat} graphs={graphElements} onOpen={() => fetchCategory(cat)} />;
    });
  }, [iframes, graphCategories]);

  // render results
  return (
    <>
      <Page documentTitle="UI Statistics">
        <Breadcrumbs
          template={BreadcrumbTemplateEnum.Performance}
          items={[
            {
              iconClass: "fa-desktop",
              text: "UI Statistics",
            },
          ]}
        />
        <PageInner>
          <PageHeader title="UI Statistics" />
          <PageSection>
            <div className="d-flex align-items-end mb-5">
              <QESSelect containerClassName="col-3" label="Timeframe" testId="ui-statistics__timeframe" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                {timeframeOptions}
              </QESSelect>
              <QESButton qesType="secondary" className="ml-3" onClick={collapseAll} qesTestId="ui-statistics__collapse-all">Close All Categories</QESButton>
            </div>
            {categoryCollapses}
          </PageSection>
        </PageInner>
      </Page>
    </>
  );
}

export const UiStatistics: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <UiStastiticsCollapseContextProvider>
      <UiStatisticsContent />
    </UiStastiticsCollapseContextProvider>
  )
}